import React, { useState, useEffect } from 'react';
import { warrantyPlanApi, formatApiErrorDetail } from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { AppLayout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  ShieldCheck,
  Plus,
  Edit2,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

export default function WarrantyPlansPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    plan_name: '',
    duration_months: 12,
    device_price_min: 0,
    device_price_max: 50000,
    warranty_price: 0,
    is_active: true,
    contract_type: 'ALL',
    os_type: 'BOTH',
  });

  const fetchPlans = async () => {
    try {
      const { data } = await warrantyPlanApi.list();
      setPlans(data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to load warranty plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const resetForm = () => {
    setFormData({
      plan_name: '', duration_months: 12, device_price_min: 0,
      device_price_max: 50000, warranty_price: 0, is_active: true, contract_type: 'ALL', os_type: 'BOTH',
    });
    setEditingPlan(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setShowDialog(true);
  };

  const handleOpenEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      plan_name: plan.plan_name, duration_months: plan.duration_months,
      device_price_min: plan.device_price_min, device_price_max: plan.device_price_max,
      warranty_price: plan.warranty_price, is_active: plan.is_active,
      contract_type: plan.contract_type || 'ALL', os_type: plan.os_type || 'BOTH',
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingPlan) {
        await warrantyPlanApi.update(editingPlan.id, formData);
        toast.success('Warranty plan updated');
      } else {
        await warrantyPlanApi.create(formData);
        toast.success('Warranty plan created');
      }
      setShowDialog(false);
      resetForm();
      fetchPlans();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to save warranty plan');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (plan) => {
    try {
      await warrantyPlanApi.update(plan.id, { is_active: !plan.is_active });
      toast.success(`Plan ${plan.is_active ? 'deactivated' : 'activated'}`);
      fetchPlans();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to update plan status');
    }
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Warranty Plans
            </h1>
            <p className="text-slate-600 mt-1">Manage warranty plan offerings</p>
          </div>
          <Button
            onClick={handleOpenCreate}
            className="bg-[#005DE8] hover:bg-[#0047B3]"
            data-testid="create-plan-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Plan
          </Button>
        </div>

        {/* Plans Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-[#005DE8]" />
              </div>
            ) : plans.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-100">
                    <TableHead>Plan Name</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Device Price Range</TableHead>
                    <TableHead className="text-right">Warranty Price</TableHead>
                    <TableHead>Contract</TableHead>
                    <TableHead>OS</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plans.map((plan) => (
                    <TableRow key={plan.id} className="table-row-hover">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                            <ShieldCheck className="h-5 w-5 text-emerald-600" />
                          </div>
                          <span className="font-medium text-slate-900">{plan.plan_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="border-slate-200">
                          {plan.duration_months} months
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {formatCurrency(plan.device_price_min)} - {formatCurrency(plan.device_price_max)}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-slate-900">
                        {formatCurrency(plan.warranty_price)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="border-slate-200 text-xs">
                          {plan.contract_type || 'ALL'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={plan.os_type === 'IOS' ? 'bg-slate-800 text-white' : plan.os_type === 'ANDROID' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}>
                          {plan.os_type || 'BOTH'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={plan.is_active}
                            onCheckedChange={() => handleToggleActive(plan)}
                            data-testid={`plan-toggle-${plan.id}`}
                          />
                          <span className={`text-sm ${plan.is_active ? 'text-emerald-600' : 'text-slate-400'}`}>
                            {plan.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenEdit(plan)}
                          data-testid={`edit-plan-${plan.id}`}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-16">
                <ShieldCheck className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">No warranty plans</h3>
                <p className="text-slate-500 mt-1">Get started by creating your first warranty plan</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>
                {editingPlan ? 'Edit Warranty Plan' : 'Create Warranty Plan'}
              </DialogTitle>
              <DialogDescription>
                {editingPlan
                  ? 'Update the warranty plan details. Changes will apply to future sales only.'
                  : 'Create a new warranty plan for your partners to sell.'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="plan_name">Plan Name</Label>
                  <Input
                    id="plan_name"
                    value={formData.plan_name}
                    onChange={(e) => setFormData({ ...formData, plan_name: e.target.value })}
                    placeholder="e.g., Basic Protection"
                    required
                    data-testid="plan-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="duration_months">Duration</Label>
                  <Select
                    value={String(formData.duration_months)}
                    onValueChange={(value) => setFormData({ ...formData, duration_months: parseInt(value) })}
                  >
                    <SelectTrigger data-testid="plan-duration-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6">6 months</SelectItem>
                      <SelectItem value="12">12 months</SelectItem>
                      <SelectItem value="24">24 months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="device_price_min">Min Device Price (INR)</Label>
                    <Input
                      id="device_price_min"
                      type="number"
                      min="0"
                      value={formData.device_price_min}
                      onChange={(e) => setFormData({ ...formData, device_price_min: parseFloat(e.target.value) || 0 })}
                      required
                      data-testid="plan-min-price"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="device_price_max">Max Device Price (INR)</Label>
                    <Input
                      id="device_price_max"
                      type="number"
                      min="0"
                      value={formData.device_price_max}
                      onChange={(e) => setFormData({ ...formData, device_price_max: parseFloat(e.target.value) || 0 })}
                      required
                      data-testid="plan-max-price"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="warranty_price">Warranty Price (INR)</Label>
                  <Input
                    id="warranty_price"
                    type="number"
                    min="0"
                    value={formData.warranty_price}
                    onChange={(e) => setFormData({ ...formData, warranty_price: parseFloat(e.target.value) || 0 })}
                    required
                    data-testid="plan-warranty-price"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Contract Type</Label>
                  <Select value={formData.contract_type} onValueChange={(v) => setFormData({ ...formData, contract_type: v })}>
                    <SelectTrigger data-testid="plan-contract-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All Tenants</SelectItem>
                      <SelectItem value="EXCLUSIVE">Exclusive Only</SelectItem>
                      <SelectItem value="NON_EXCLUSIVE">Non-Exclusive Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>OS Type *</Label>
                  <Select value={formData.os_type} onValueChange={(v) => setFormData({ ...formData, os_type: v })}>
                    <SelectTrigger data-testid="plan-os-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BOTH">Both (Android + iOS)</SelectItem>
                      <SelectItem value="ANDROID">Android Only</SelectItem>
                      <SelectItem value="IOS">iOS Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="is_active">Active</Label>
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    data-testid="plan-active-switch"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#005DE8] hover:bg-[#0047B3]"
                  disabled={saving}
                  data-testid="plan-submit-button"
                >
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  {editingPlan ? 'Update Plan' : 'Create Plan'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
